import type { AuditAction } from '@obraplan/core/domain/audit/audit-log';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { useContainer, useSession } from '@/features/session/use-session';
import { formatDateTime } from '@/lib/format';
import { useLive } from '@/lib/use-live';

const ACTION_LABEL: Readonly<Record<AuditAction, string>> = {
  CREATE: 'Criação',
  UPDATE: 'Alteração',
  SOFT_DELETE: 'Exclusão',
  RESTORE: 'Restauração',
  STATUS_CHANGE: 'Mudança de status',
  USER_SWITCH: 'Troca de perfil',
  SIGN_IN: 'Entrada no sistema',
  SIGN_OUT: 'Saída do sistema',
  SESSION_REVOKE: 'Acesso revogado',
  API_KEY_CREATE: 'Chave de API criada',
  API_KEY_REVOKE: 'Chave de API revogada',
  LOGIN_LINK_SENT: 'Link de entrada enviado',
  IMPORT_CONFIRM: 'Importação confirmada',
  PERMISSION_CHANGE: 'Mudança de papel',
  CAPACITY_OVERRIDE: 'Capacidade excedida',
  INSPECTION_CONFIRM: 'Conferência confirmada',
  INSPECTION_ADJUST: 'Conferência ajustada',
  INSPECTION_ABSENCE: 'Ausência registrada',
  INSPECTION_REOPEN: 'Conferência reaberta',
  INSPECTION_MEASURE: 'Medição registrada',
  REPORT_OPEN: 'Relatório diário aberto',
  REPORT_CLOSE: 'Relatório diário emitido',
  REPORT_REVISE: 'Relatório diário em revisão',
  REPORT_OUTDATED: 'Relatório diário desatualizado',
  REPORT_WEATHER: 'Tempo e turnos do dia',
  REPORT_TASKS: 'Tarefas avulsas do dia',
  REPORT_NOTES: 'Observação do dia',
  REPORT_CREW: 'Efetivo do dia',
  REPORT_EQUIPMENT: 'Equipamento do dia',
  CHECKLIST_SAVE: 'Checklist de qualidade gravada',
  CHECKLIST_TOGGLE: 'Checklist de qualidade ativada ou desativada',
  SERVICE_VERIFY: 'Verificação de qualidade',
  NON_CONFORMITY_OPEN: 'Não conformidade aberta',
  NON_CONFORMITY_CANCEL: 'Não conformidade cancelada',
  NON_CONFORMITY_RESOLVE: 'Não conformidade encerrada',
  BACKUP_EXPORT: 'Backup exportado',
  BACKUP_IMPORT: 'Backup restaurado',
  INTEGRITY_REPAIR: 'Integridade corrigida',
  STORAGE_PURGE: 'Linhas de importação expurgadas',
  STORAGE_ARCHIVE: 'Fotos arquivadas',
  SUPPLY_ACKNOWLEDGE: 'Ciência de material',
  DEMO_DATA_LOAD: 'Dados de demonstração',
};

const ACTION_TONE: Readonly<Record<AuditAction, 'neutral' | 'ok' | 'info' | 'warn' | 'risk'>> = {
  CREATE: 'ok',
  UPDATE: 'info',
  SOFT_DELETE: 'warn',
  RESTORE: 'info',
  STATUS_CHANGE: 'info',
  USER_SWITCH: 'neutral',
  SIGN_IN: 'neutral',
  SIGN_OUT: 'neutral',
  SESSION_REVOKE: 'warn',
  API_KEY_CREATE: 'ok',
  API_KEY_REVOKE: 'warn',
  LOGIN_LINK_SENT: 'neutral',
  IMPORT_CONFIRM: 'ok',
  PERMISSION_CHANGE: 'warn',
  CAPACITY_OVERRIDE: 'risk',
  INSPECTION_CONFIRM: 'ok',
  INSPECTION_ADJUST: 'info',
  INSPECTION_ABSENCE: 'warn',
  INSPECTION_REOPEN: 'warn',
  INSPECTION_MEASURE: 'ok',
  REPORT_OPEN: 'neutral',
  REPORT_CLOSE: 'ok',
  // Revisar é desfazer o que já foi entregue; desatualizar é descobrir que o que foi
  // entregue deixou de valer. Nenhum dos dois é rotina, e nenhum é acidente.
  REPORT_REVISE: 'warn',
  REPORT_OUTDATED: 'warn',
  REPORT_WEATHER: 'info',
  REPORT_TASKS: 'info',
  REPORT_NOTES: 'info',
  REPORT_CREW: 'info',
  REPORT_EQUIPMENT: 'info',
  CHECKLIST_SAVE: 'info',
  CHECKLIST_TOGGLE: 'neutral',
  SERVICE_VERIFY: 'ok',
  // A NC é o que o documento existe para registrar; abrir uma é fato do dia, não erro.
  NON_CONFORMITY_OPEN: 'warn',
  NON_CONFORMITY_CANCEL: 'neutral',
  NON_CONFORMITY_RESOLVE: 'ok',
  BACKUP_EXPORT: 'ok',
  BACKUP_IMPORT: 'warn',
  INTEGRITY_REPAIR: 'warn',
  STORAGE_PURGE: 'warn',
  STORAGE_ARCHIVE: 'warn',
  SUPPLY_ACKNOWLEDGE: 'ok',
  DEMO_DATA_LOAD: 'neutral',
};

/** Valor de metadado em texto — `String(objeto)` daria `[object Object]` e perderia o detalhe. */
function describeValue(value: unknown): string {
  if (value === null || typeof value !== 'object') return String(value);
  if (Array.isArray(value)) return value.map(describeValue).join(', ');
  return Object.entries(value)
    .map(([key, inner]) => key + ' ×' + describeValue(inner))
    .join(', ');
}

/**
 * Trilha de auditoria.
 *
 * Toda operação sensível grava aqui, dentro da mesma transação da operação (RN-113).
 * A tabela é *append-only* (RN-112) — a tela é somente leitura por construção, porque
 * não existe caso de uso de alteração para chamar.
 */
export function AuditPage() {
  const container = useContainer();
  const session = useSession();

  const result = useLive(() => container.listRecentAudit.execute(session, 200), [session]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Auditoria</h1>
        <p className="text-(--color-content-muted)">
          Quem fez o quê, quando. O registro nunca é alterado nem apagado.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Registros recentes</CardTitle>
          <CardDescription>
            Últimas 200 operações, da mais recente para a mais antiga.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {result === undefined ? (
            <LoadingState />
          ) : !result.ok ? (
            <ErrorState message={result.error.message} code={result.error.code} />
          ) : result.value.length === 0 ? (
            <EmptyState title="Nenhum registro ainda" />
          ) : (
            <TableWrapper>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quando</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Entidade</TableHead>
                    <TableHead>Detalhe</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.value.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="whitespace-nowrap tabular">
                        {formatDateTime(entry.occurredAt)}
                      </TableCell>
                      <TableCell>
                        <Badge tone={ACTION_TONE[entry.action]}>{ACTION_LABEL[entry.action]}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{entry.entityType}</TableCell>
                      <TableCell className="max-w-md truncate text-sm text-(--color-content-muted)">
                        {entry.changes !== undefined
                          ? Object.entries(entry.changes)
                              .map(
                                ([field, change]) =>
                                  field + ': ' + String(change.from) + ' → ' + String(change.to),
                              )
                              .join(' · ')
                          : entry.metadata !== undefined
                            ? Object.entries(entry.metadata)
                                .map(([key, value]) => key + ': ' + describeValue(value))
                                .join(' · ')
                            : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
